import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building2, Globe, Phone, Mail, Package, Wrench,
  Shield, Clock, Edit, Plus, Trash2, X, Save, ExternalLink,
  Search, ChevronRight, Users, MapPin, CreditCard, Truck
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const DEPARTMENTS = [
  { value: 'sav', label: 'SAV', color: '#EF4444' },
  { value: 'commercial', label: 'Commercial', color: '#3B82F6' },
  { value: 'marketing', label: 'Marketing', color: '#EC4899' },
  { value: 'production', label: 'Production', color: '#F59E0B' },
  { value: 'logistics', label: 'Logistique', color: '#14B8A6' },
  { value: 'administration', label: 'Administration', color: '#8B5CF6' },
  { value: 'direction', label: 'Direction', color: '#00E01A' },
  { value: 'technique', label: 'Technique', color: '#F97316' },
];

const relationLabels = {
  exclusive_importer: 'Importateur exclusif',
  authorized_reseller: 'Revendeur agréé',
  distributor: 'Distributeur'
};

const statusColors = {
  active: 'bg-[#142915] text-[#00E01A] border-[#00E01A]/20',
  inactive: 'bg-[#2C3338] text-[#8C8F94] border-[#2C3338]',
  suspended: 'bg-[#2A1515] text-[#E74C3C] border-[#E74C3C]/20'
};

const statusLabels = { active: 'Actif', inactive: 'Inactif', suspended: 'Suspendu' };

function getDeptInfo(dept) {
  return DEPARTMENTS.find(d => d.value === dept) || { label: dept, color: '#8C8F94' };
}

// ========== PRODUCT CARD ==========
function ProductCard({ product }) {
  const photoUrl = (product.photos && product.photos.length > 0) ? product.photos[0] : null;

  return (
    <div className="bg-[#1D2327] rounded-lg border border-[#2C3338] p-3 hover:border-[#00E01A]/30 transition-all group" data-testid={`product-card-${product.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-14 h-14 bg-[#141617] rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
          {photoUrl ? (
            <img src={photoUrl} alt={product.name} className="w-full h-full object-cover rounded-lg" />
          ) : (
            <Package size={20} className="text-[#52555A]" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium truncate">{product.name}</p>
          <p className="text-[#8C8F94] text-xs font-mono">{product.reference}</p>
          <div className="flex items-center gap-2 mt-1">
            {product.category && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#2C3338] text-[#8C8F94]">{product.category}</span>
            )}
            {product.sell_price_ttc != null && (
              <span className="text-[#00E01A] text-xs font-semibold">{product.sell_price_ttc}€</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ========== CONTACT CARD ==========
function ContactCard({ contact, onEdit, onDelete }) {
  const dept = getDeptInfo(contact.department);
  const initials = ((contact.first_name?.[0] || '') + (contact.last_name?.[0] || '?')).toUpperCase();

  return (
    <div className="bg-[#1D2327] rounded-lg border border-[#2C3338] p-4 hover:border-[#00E01A]/10 transition-all" data-testid={`contact-card-${contact.department}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
          style={{ backgroundColor: dept.color }}>
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-white text-sm font-medium">
              {[contact.first_name, contact.last_name].filter(Boolean).join(' ') || '—'}
            </span>
            <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase"
              style={{ backgroundColor: dept.color + '20', color: dept.color }}>
              {dept.label}
            </span>
          </div>
          {contact.position && <p className="text-[#8C8F94] text-xs mt-0.5">{contact.position}</p>}
          <div className="flex flex-wrap gap-3 mt-2">
            {contact.email && (
              <a href={`mailto:${contact.email}`} className="flex items-center gap-1 text-xs text-[#8C8F94] hover:text-[#00E01A] transition-colors">
                <Mail size={11} /> {contact.email}
              </a>
            )}
            {contact.phone && (
              <a href={`tel:${contact.phone}`} className="flex items-center gap-1 text-xs text-[#8C8F94] hover:text-[#00E01A] transition-colors">
                <Phone size={11} /> {contact.phone}
              </a>
            )}
          </div>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <button onClick={() => onEdit(contact)} className="text-[#8C8F94] hover:text-white p-1 transition-colors"><Edit size={13} /></button>
          <button onClick={() => onDelete(contact)} className="text-[#8C8F94] hover:text-[#EF4444] p-1 transition-colors"><Trash2 size={13} /></button>
        </div>
      </div>
    </div>
  );
}

// ========== MAIN COMPONENT ==========
export default function SupplierDetail() {
  const { api } = useAuth();
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productSearch, setProductSearch] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({
    department: 'sav', first_name: '', last_name: '', position: '', email: '', phone: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [supplierRes, productsRes] = await Promise.all([
        api.get(`/suppliers/${supplierId}`),
        api.get(`/products?supplier_id=${supplierId}`),
      ]);
      setSupplier(supplierRes.data);
      setProducts(productsRes.data);
    } catch (e) {
      toast.error("Fournisseur non trouvé");
    } finally {
      setLoading(false);
    }
  }, [api, supplierId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Contact CRUD
  const saveContacts = async (newContacts) => {
    if (!supplier) return;
    const payload = {
      name: supplier.name,
      country: supplier.country,
      website: supplier.website,
      relation_type: supplier.relation_type,
      default_warranty_months: supplier.default_warranty_months,
      sav_portal_url: supplier.sav_portal_url,
      status: supplier.status,
      contacts: newContacts,
      logo_url: supplier.logo_url,
      discount_percent: supplier.discount_percent,
      payment_terms: supplier.payment_terms,
      avg_delivery_days: supplier.avg_delivery_days,
      min_order: supplier.min_order,
      labor_rate_per_hour: supplier.labor_rate_per_hour,
    };
    await api.put(`/suppliers/${supplierId}`, payload);
    fetchData();
  };

  const handleSaveContact = async () => {
    if (!contactForm.last_name && !contactForm.email) { toast.error("Nom ou email requis"); return; }
    const contacts = [...(supplier.contacts || [])];
    if (editingContact !== null) {
      contacts[editingContact] = contactForm;
    } else {
      contacts.push(contactForm);
    }
    await saveContacts(contacts);
    toast.success(editingContact !== null ? "Contact modifié" : "Contact ajouté");
    resetContactForm();
  };

  const handleDeleteContact = async (contact) => {
    const contacts = (supplier.contacts || []).filter(c => c !== contact);
    await saveContacts(contacts);
    toast.success("Contact supprimé");
  };

  const handleEditContact = (contact) => {
    const idx = (supplier.contacts || []).indexOf(contact);
    setEditingContact(idx);
    setContactForm({ ...contact });
    setShowContactForm(true);
  };

  const resetContactForm = () => {
    setContactForm({ department: 'sav', first_name: '', last_name: '', position: '', email: '', phone: '' });
    setEditingContact(null);
    setShowContactForm(false);
  };

  // Product filtering
  const categories = [...new Set(products.map(p => p.category).filter(Boolean))].sort();
  const filteredProducts = products.filter(p => {
    if (productSearch) {
      const q = productSearch.toLowerCase();
      if (!p.name?.toLowerCase().includes(q) && !p.reference?.toLowerCase().includes(q)) return false;
    }
    if (productCategory && p.category !== productCategory) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-2 border-[#00E01A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="p-6 text-center">
        <Building2 size={48} className="mx-auto text-[#52555A] mb-3" />
        <p className="text-[#8C8F94]">Fournisseur non trouvé</p>
        <Link to="/suppliers" className="text-[#00E01A] hover:underline mt-2 inline-block">Retour</Link>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="supplier-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/suppliers" className="p-2 rounded-lg bg-[#1D2327] border border-[#2C3338] text-[#8C8F94] hover:text-white hover:border-[#00E01A]/30 transition-colors" data-testid="back-to-suppliers">
          <ArrowLeft size={20} />
        </Link>
        <div className="flex items-center gap-4 flex-1">
          <div className="w-14 h-14 bg-[#1D2327] rounded-xl flex items-center justify-center border border-[#2C3338]">
            {supplier.logo_url ? (
              <img src={supplier.logo_url} alt={supplier.name} className="w-11 h-11 object-contain" />
            ) : (
              <Building2 size={28} className="text-[#8C8F94]" />
            )}
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">{supplier.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${statusColors[supplier.status]}`}>
                {statusLabels[supplier.status]}
              </span>
              <span className="text-[#8C8F94] text-xs">{relationLabels[supplier.relation_type]}</span>
              <span className="text-[#52555A] text-xs">•</span>
              <span className="text-[#8C8F94] text-xs flex items-center gap-1"><MapPin size={10} />{supplier.country}</span>
            </div>
          </div>
        </div>
        <button
          onClick={() => navigate(`/suppliers`)}
          className="text-[#8C8F94] hover:text-white text-sm"
        >
        </button>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#00E01A]/10">
            <Package size={18} className="text-[#00E01A]" />
          </div>
          <div>
            <p className="text-xl font-bold text-white">{supplier.product_count}</p>
            <p className="text-[#8C8F94] text-[10px] uppercase tracking-wider">Produits</p>
          </div>
        </div>
        <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#3B82F6]/10">
            <Users size={18} className="text-[#3B82F6]" />
          </div>
          <div>
            <p className="text-xl font-bold text-white">{(supplier.contacts || []).length}</p>
            <p className="text-[#8C8F94] text-[10px] uppercase tracking-wider">Contacts</p>
          </div>
        </div>
        <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#F59E0B]/10">
            <Wrench size={18} className="text-[#F59E0B]" />
          </div>
          <div>
            <p className="text-xl font-bold text-white">{supplier.active_sav_count}</p>
            <p className="text-[#8C8F94] text-[10px] uppercase tracking-wider">SAV actifs</p>
          </div>
        </div>
        <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#8B5CF6]/10">
            <Shield size={18} className="text-[#8B5CF6]" />
          </div>
          <div>
            <p className="text-xl font-bold text-white">{supplier.default_warranty_months}m</p>
            <p className="text-[#8C8F94] text-[10px] uppercase tracking-wider">Garantie</p>
          </div>
        </div>
      </div>

      {/* Two columns: Info + Contacts | Products */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Left column: Info & Contacts */}
        <div className="space-y-5">
          {/* Info */}
          <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-5 space-y-3" data-testid="supplier-info">
            <h3 className="text-white font-semibold text-sm">Informations</h3>
            {supplier.website && (
              <a href={supplier.website} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 text-[#8C8F94] hover:text-[#00E01A] text-sm transition-colors">
                <Globe size={14} /> {supplier.website} <ExternalLink size={10} />
              </a>
            )}
            {supplier.sav_portal_url && (
              <a href={supplier.sav_portal_url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 text-[#8C8F94] hover:text-[#00E01A] text-sm transition-colors">
                <Wrench size={14} /> Portail SAV <ExternalLink size={10} />
              </a>
            )}
            {supplier.labor_rate_per_hour && (
              <div className="flex items-center gap-2 text-[#8C8F94] text-sm">
                <CreditCard size={14} /> Tarif MO : <span className="text-white font-medium">{supplier.labor_rate_per_hour}€/h</span>
              </div>
            )}
            {supplier.discount_percent && (
              <div className="flex items-center gap-2 text-[#8C8F94] text-sm">
                <CreditCard size={14} /> Remise : <span className="text-white font-medium">{supplier.discount_percent}%</span>
              </div>
            )}
            {supplier.payment_terms && (
              <div className="flex items-center gap-2 text-[#8C8F94] text-sm">
                <Clock size={14} /> Conditions : <span className="text-white">{supplier.payment_terms}</span>
              </div>
            )}
            {supplier.avg_delivery_days && (
              <div className="flex items-center gap-2 text-[#8C8F94] text-sm">
                <Truck size={14} /> Livraison : <span className="text-white">{supplier.avg_delivery_days} jours</span>
              </div>
            )}
          </div>

          {/* Contacts */}
          <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-5" data-testid="supplier-contacts-section">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold text-sm">Contacts ({(supplier.contacts || []).length})</h3>
              <button
                onClick={() => { resetContactForm(); setShowContactForm(true); }}
                className="flex items-center gap-1 text-xs text-[#00E01A] hover:text-[#00B815] transition-colors"
                data-testid="add-contact-detail-btn"
              >
                <Plus size={14} /> Ajouter
              </button>
            </div>

            <div className="space-y-2">
              {(supplier.contacts || []).length === 0 ? (
                <p className="text-[#52555A] text-sm text-center py-4">Aucun contact enregistré</p>
              ) : (
                (supplier.contacts || []).map((c, idx) => (
                  <ContactCard key={idx} contact={c} onEdit={handleEditContact} onDelete={handleDeleteContact} />
                ))
              )}
            </div>

            {/* Contact form dialog */}
            <Dialog open={showContactForm} onOpenChange={(open) => { if (!open) resetContactForm(); }}>
              <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-lg font-bold font-['Outfit']">
                    {editingContact !== null ? 'Modifier le contact' : 'Nouveau contact'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Service *</Label>
                    <Select value={contactForm.department} onValueChange={v => setContactForm(p => ({...p, department: v}))}>
                      <SelectTrigger data-testid="detail-contact-department"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DEPARTMENTS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Prénom</Label>
                      <Input value={contactForm.first_name} onChange={e => setContactForm(p => ({...p, first_name: e.target.value}))} data-testid="detail-contact-firstname" />
                    </div>
                    <div>
                      <Label className="text-xs">Nom</Label>
                      <Input value={contactForm.last_name} onChange={e => setContactForm(p => ({...p, last_name: e.target.value}))} data-testid="detail-contact-lastname" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Poste</Label>
                    <Input value={contactForm.position} onChange={e => setContactForm(p => ({...p, position: e.target.value}))} placeholder="Resp. SAV Europe" data-testid="detail-contact-position" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Email</Label>
                      <Input type="email" value={contactForm.email} onChange={e => setContactForm(p => ({...p, email: e.target.value}))} data-testid="detail-contact-email" />
                    </div>
                    <div>
                      <Label className="text-xs">Téléphone</Label>
                      <Input value={contactForm.phone} onChange={e => setContactForm(p => ({...p, phone: e.target.value}))} data-testid="detail-contact-phone" />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button className="bg-[#00E01A] hover:bg-[#00B815] text-black" onClick={handleSaveContact} data-testid="save-detail-contact-btn">
                      {editingContact !== null ? 'Modifier' : 'Ajouter'}
                    </Button>
                    <Button variant="outline" onClick={resetContactForm}>Annuler</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Right column: Products (2/3 width) */}
        <div className="lg:col-span-2" data-testid="supplier-products-section">
          <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h3 className="text-white font-semibold text-sm">
                Catalogue ({filteredProducts.length}{productSearch || productCategory ? ` / ${products.length}` : ''})
              </h3>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#52555A]" />
                  <input
                    className="bg-[#141617] border border-[#2C3338] rounded-lg pl-8 pr-3 py-2 text-white text-xs placeholder:text-[#52555A] focus:border-[#00E01A] focus:outline-none w-48"
                    placeholder="Rechercher un produit..."
                    value={productSearch}
                    onChange={e => setProductSearch(e.target.value)}
                    data-testid="product-search"
                  />
                </div>
                {categories.length > 1 && (
                  <select
                    className="bg-[#141617] border border-[#2C3338] rounded-lg px-3 py-2 text-white text-xs focus:border-[#00E01A] focus:outline-none"
                    value={productCategory}
                    onChange={e => setProductCategory(e.target.value)}
                    data-testid="product-category-filter"
                  >
                    <option value="">Toutes catégories</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                )}
              </div>
            </div>

            {filteredProducts.length === 0 ? (
              <div className="text-center py-8">
                <Package size={36} className="mx-auto text-[#52555A] mb-2" />
                <p className="text-[#8C8F94] text-sm">
                  {productSearch || productCategory ? 'Aucun produit trouvé' : 'Aucun produit dans le catalogue'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[600px] overflow-y-auto pr-1" data-testid="products-grid">
                {filteredProducts.map(p => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
